import { useCallback, useEffect, useRef, useState } from "react";
import type { PillContent } from "@/desktop/compact-cursor-pill-content";
import type { AgentCursor } from "@/stores/canvas-store";

export const LIVE_CURSOR_REPLY_HOLD_MS = 7_000;

/** Dismiss one idle reply, keeping the run and its next response available. */
export function useLiveCursorReply(
  cursor: AgentCursor | null,
  content: PillContent | null,
  hovered: boolean,
  presenting: boolean,
) {
  const live = content?.kind === "reply" && !content.terminal && !presenting;
  const key =
    live && cursor
      ? JSON.stringify([
          cursor.agentId,
          cursor.executionId,
          cursor.sessionId,
          content.text,
        ])
      : null;
  const scope = cursor
    ? JSON.stringify([cursor.agentId, cursor.executionId, cursor.sessionId])
    : null;
  const lastReply = useRef({ scope, key });
  if (scope !== lastReply.current.scope || key) {
    lastReply.current = { scope, key };
  }
  // Tool updates can omit or clear assistant text. Keep counting the last
  // reply's idle time so the same text cannot gain another hold every tool.
  const timerKey =
    presenting || cursor?.frozen || cursor?.question || cursor?.error
      ? null
      : lastReply.current.key;
  const paused = hovered && live;
  const [hiddenKey, setHiddenKey] = useState<string | null>(null);
  const hidden = key !== null && key === hiddenKey;

  useEffect(() => {
    if (!timerKey || hiddenKey === timerKey || paused) return;
    // Text chunks restart the reading window; pointer and status refreshes
    // do not. Hover holds the reply until the user leaves it again.
    const timer = window.setTimeout(
      () => setHiddenKey(timerKey),
      LIVE_CURSOR_REPLY_HOLD_MS,
    );
    return () => window.clearTimeout(timer);
  }, [timerKey, hiddenKey, paused]);

  const dismiss = useCallback(() => {
    if (key) setHiddenKey(key);
  }, [key]);

  return { live, hidden, dismiss };
}
